import {
  sendEmailVerification,
  sendPasswordResetEmail,
  verifyBeforeUpdateEmail,
  User,
  applyActionCode,
  confirmPasswordReset,
  checkActionCode,
  AuthError
} from 'firebase/auth';
import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import SystemEmailService from './systemEmailService';

// Email verification service
export class EmailAuthService {
  // Send email verification using passcode flow (standardized)
  static async sendVerificationEmail(user: User): Promise<{ success: boolean; error?: string }> {
    try {
      const { PasscodeVerificationService } = await import('./passcodeVerificationService');
      return await PasscodeVerificationService.sendEmailVerificationPasscode(user, true);
    } catch (error) {
      console.error('Error sending verification email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send verification email'
      };
    }
  }

  // Send custom verification email via SES
  static async sendCustomVerificationEmail(
    user: User,
    verificationLink: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { default: EmailService, PASSCODE_VERIFICATION_FROM_EMAIL } = await import('./emailService');

      const result = await EmailService.sendEmail({
        to: user.email || '',
        subject: 'Verify Your Email Address - Scubamail',
        htmlBody: SystemEmailService.getVerificationEmail(user.displayName || 'User', verificationLink),
        textBody: `Welcome to Scubamail! Please verify your email address by visiting: ${verificationLink}`,
        fromEmail: PASSCODE_VERIFICATION_FROM_EMAIL,
        fromName: 'Scubamail',
        tags: {
          Type: 'EmailVerification',
          UserId: user.uid
        }
      });
      return result;
    } catch (error) {
      console.error('Error sending custom verification email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send verification email'
      };
    }
  }

  // Send password reset email using passcode flow (standardized)
  static async sendPasswordResetEmailToUser(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { PasscodeVerificationService } = await import('./passcodeVerificationService');
      return await PasscodeVerificationService.sendPasswordResetPasscode(email);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send password reset email'
      };
    }
  }

  // Send custom password reset email via SES
  static async sendCustomPasswordResetEmail(
    email: string,
    resetLink: string,
    userName?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { default: EmailService } = await import('./emailService');
      const fromEmail = process.env.REACT_APP_FROM_EMAIL || 'info@scubamail.io';

      const result = await EmailService.sendEmail({
        to: email,
        subject: 'Reset Your Password - Scubamail',
        htmlBody: SystemEmailService.getPasswordResetEmail(userName || 'User', resetLink),
        textBody: `Reset your Scubamail password by visiting: ${resetLink}`,
        fromEmail: fromEmail,
        fromName: 'Scubamail',
        tags: {
          Type: 'PasswordReset',
          Email: email
        }
      });
      return result;
    } catch (error) {
      console.error('Error sending custom password reset email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send password reset email'
      };
    }
  }

  // Verify email with action code
  static async verifyEmailWithCode(actionCode: string): Promise<{ success: boolean; error?: string }> {
    try {
      await applyActionCode(auth, actionCode);
      return { success: true };
    } catch (error) {
      console.error('Error verifying email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to verify email'
      };
    }
  }

  // Confirm password reset
  static async confirmPasswordReset(actionCode: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      return { success: true };
    } catch (error) {
      console.error('Error confirming password reset:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reset password'
      };
    }
  }

  // Reset password using custom passcode (calls Backend Cloud Function)
  static async resetPasswordWithPasscode(email: string, passcode: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      const resetPasswordFn = httpsCallable(functions, 'resetPasswordWithPasscode');
      await resetPasswordFn({ email, passcode, newPassword });
      return { success: true };
    } catch (error: any) {
      console.error('Error resetting password with passcode:', error);
      return {
        success: false,
        error: error.message || 'Failed to reset password with passcode'
      };
    }
  }

  // Check if action code is valid
  static async checkActionCodeValidity(actionCode: string): Promise<{ valid: boolean; operation?: string; email?: string; error?: string }> {
    try {
      const info = await checkActionCode(auth, actionCode);
      return {
        valid: true,
        operation: info.operation,
        email: info.data?.email || undefined
      };
    } catch (error) {
      console.error('Error checking action code:', error);
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Invalid action code'
      };
    }
  }

  // Update email with verification
  static async updateEmailWithVerification(user: User, newEmail: string): Promise<{ success: boolean; error?: string }> {
    try {
      await verifyBeforeUpdateEmail(user, newEmail);
      return { success: true };
    } catch (error) {
      console.error('Error updating email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update email'
      };
    }
  }
}

export default EmailAuthService;
